import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext(null);

const TOKEN_KEY = 'equifax_auth_token';

export function AuthProvider({ children }) {
	const [user, setUser] = useState(null);
	const [loading, setLoading] = useState(true);
	const [needsSetup, setNeedsSetup] = useState(false);

	const getToken = useCallback(() => localStorage.getItem(TOKEN_KEY), []);

	// Check auth status on mount
	useEffect(() => {
		async function checkAuth() {
			const token = localStorage.getItem(TOKEN_KEY);
			if (!token) {
				// Check if setup is needed
				try {
					const res = await fetch('/server/equifax_function/api/auth/status');
					const data = await res.json();
					setNeedsSetup(data.needsSetup);
				} catch {
					setNeedsSetup(false);
				}
				setLoading(false);
				return;
			}

			try {
				const res = await fetch('/server/equifax_function/api/auth/me', {
					headers: { 'X-App-Token': token }
				});
				if (res.ok) {
					const data = await res.json();
					setUser(data.user);
				} else {
					localStorage.removeItem(TOKEN_KEY);
					// Check setup status
					const statusRes = await fetch('/server/equifax_function/api/auth/status');
					const statusData = await statusRes.json();
					setNeedsSetup(statusData.needsSetup);
				}
			} catch {
				localStorage.removeItem(TOKEN_KEY);
			}
			setLoading(false);
		}
		checkAuth();
	}, []);

	async function login(email, password) {
		const res = await fetch('/server/equifax_function/api/auth/login', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ email, password })
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Login failed');
		localStorage.setItem(TOKEN_KEY, data.token);
		setUser(data.user);
		setNeedsSetup(false);
		return data;
	}

	async function setup(password) {
		const res = await fetch('/server/equifax_function/api/auth/setup', {
			method: 'POST',
			headers: { 'Content-Type': 'application/json' },
			body: JSON.stringify({ password })
		});
		const data = await res.json();
		if (!res.ok) throw new Error(data.error || 'Setup failed');
		localStorage.setItem(TOKEN_KEY, data.token);
		setUser(data.user);
		setNeedsSetup(false);
		return data;
	}

	function logout() {
		localStorage.removeItem(TOKEN_KEY);
		setUser(null);
	}

	return (
		<AuthContext.Provider value={{ user, loading, needsSetup, login, setup, logout, getToken }}>
			{children}
		</AuthContext.Provider>
	);
}

export function useAuth() {
	const ctx = useContext(AuthContext);
	if (!ctx) throw new Error('useAuth must be used within AuthProvider');
	return ctx;
}
